import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "@emotion/react/jsx-runtime";
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { ArrowRight } from '@lusha/icons';
import { PLATFORM } from '@lusha/source-platforms';
import { useAnalytics } from '@lusha-engage/analytics';
import { Button } from '@lusha-ui/button';
import { Dropdown, DropdownItem, StyledDropdownItem, StyledDropdownInputText } from '@lusha-ui/dropdown';
import { CheckIcon, InfoDarkIcon, ChevronSmallDownIcon } from '@lusha-ui/icons';
import { Label } from '@lusha-ui/label';
import { Text } from '@lusha-ui/text';
import { colors } from '@lusha-ui/theme';
import { getTooltipProps, Tooltip } from '@lusha-ui/tooltip';
import { useAppSelector } from '@reducers/index';
import { selectPlanByCode, selectPlanFeatures, selectPlanPrices, selectPlanSeatPrice, selectPlanSeatProduct, } from '@reducers/pricing/selectors';
import React from 'react';
import { BILLING_CYCLES, DEFAULT_EXPANDED_PLAN, DYNAMIC_PLANS } from '../constants';
const StyledDropdown = styled(Dropdown) `
  ${StyledDropdownItem},
  ${StyledDropdownInputText} {
    font-size: 13px !important;
  }

  @media only screen and (max-height: 600px) {
    max-height: 200px;
  }
`;
const PlanCard = ({ planCode, billingCycle, selectedCredits, selectedSeats, isExpanded, onExpand, onCreditsChange, onSeatsChange, handleClose, }) => {
    var _a, _b, _c, _d;
    const { platform, pricing, openSource } = useAppSelector(state => ({
        platform: state.settings.platform,
        pricing: state.pricing,
        openSource: state.popups.upgradePlan.source,
    }));
    const isMonthly = billingCycle === BILLING_CYCLES.MONTHLY;
    const analytics = useAnalytics();
    const billingInterval = isMonthly ? 'month' : 'year';
    const { name: planName, baseSeatAmount, featuresTitle } = DYNAMIC_PLANS[planCode];
    const plan = selectPlanByCode(pricing, planCode);
    const currentPrices = selectPlanPrices(pricing, planCode, billingInterval);
    const selectedPrice = currentPrices[selectedCredits];
    const seatProductsSteps = (_b = (_a = selectPlanSeatPrice(pricing, planCode, billingInterval)) === null || _a === void 0 ? void 0 : _a.quantitySteps) !== null && _b !== void 0 ? _b : [];
    const selectedSeat = seatProductsSteps[selectedSeats];
    const planFeatures = selectPlanFeatures(pricing, planCode);
    const totalSum = ((_c = selectedSeat === null || selectedSeat === void 0 ? void 0 : selectedSeat.finalPrice) !== null && _c !== void 0 ? _c : 0) + ((_d = selectedPrice === null || selectedPrice === void 0 ? void 0 : selectedPrice.totalPrice) !== null && _d !== void 0 ? _d : 0);
    const rawMonthlyPrice = isMonthly ? totalSum : totalSum / 12;
    const roundedPrice = Math.ceil(rawMonthlyPrice * 20) / 20;
    const totalPrice = roundedPrice % 1 === 0 ? `$${roundedPrice}` : `$${roundedPrice.toFixed(2)}`;
    if (!plan) {
        return null;
    }
    function handleContinue() {
        const basePriceId = selectedPrice.id.replace(/-(?:Monthly|Yearly)-\d+$/, '');
        const seatProduct = selectPlanSeatProduct(pricing, planCode);
        const seatPrice = selectPlanSeatPrice(pricing, planCode, billingInterval);
        const params = new URLSearchParams({
            plan: plan.id,
            price: basePriceId,
            'product-price': selectedPrice.dynamicProductPriceId,
            quantity: String(selectedCredits),
            source: 'plugin_upgrade_popup',
            sub_platform: platform,
            platform: PLATFORM.PLUGIN,
            'extra-seat-product': seatProduct === null || seatProduct === void 0 ? void 0 : seatProduct.id,
            'extra-seat-price': seatPrice === null || seatPrice === void 0 ? void 0 : seatPrice.id,
            'seat-quantity': String(selectedSeats),
        });
        analytics.track({
            eventType: 'pricing_choose_plan_clicked',
            event_value: planCode,
            event_additional_info: {
                selected_plan: planCode,
                plugin_click_source: openSource,
                billing_cycle: billingCycle,
                credits: selectedPrice === null || selectedPrice === void 0 ? void 0 : selectedPrice.totalCredits,
                seats: selectedSeats + baseSeatAmount,
                default_plan: DEFAULT_EXPANDED_PLAN,
            },
        });
        window.open(`${envConfig.BILLING_CHECKOUT_URL}/checkout/v3?${params.toString()}`, '_blank');
        handleClose();
    }
    const tooltipId = `upgrade-popup-${planCode}-feature-tooltip`;
    const basePlanLabel = (_jsx(Label, { css: css `
        margin-left: 4px;
      `, backgroundColor: "#dddddd", textColor: colors.black, text: "Base plan" }));
    return (_jsxs("div", { "data-plan-code": planCode, onClick: !isExpanded ? onExpand : undefined, css: css `
        padding: 16px;
        border-radius: 16px;
        border: 1px solid ${isExpanded ? colors.stroke.brand.DEFAULT : colors.semiLightGrey};
        background: ${isExpanded ? colors.background.surface.brand.accent['1'].DEFAULT : colors.white};
        box-shadow: ${isExpanded ? '0 8px 16px 0 rgba(21, 20, 23, 0.1)' : 'none'};
        cursor: ${isExpanded ? 'default' : 'pointer'};
      `, children: [_jsxs("div", { css: css `
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 4px;
        `, children: [_jsx(Text, { variant: "h4-s", children: planName }), _jsxs(Text, { variant: "h3-s", children: [totalPrice, " /mo."] })] }), _jsx("div", { css: css `
          margin-bottom: 8px;
        `, children: _jsx(Text, { variant: "body-2-r", children: isMonthly ? 'Billed monthly' : 'Billed annually' }) }), !isExpanded && (_jsxs("div", { css: css `
            margin-bottom: 8px;
            display: flex;
            align-items: center;
            justify-content: space-between;
          `, children: [_jsxs(Text, { variant: "body-2-r", children: [selectedPrice === null || selectedPrice === void 0 ? void 0 : selectedPrice.totalCredits, " Credits/", isMonthly ? 'month' : 'year', " \u00B7", ' ', selectedSeats + baseSeatAmount, " seats"] }), _jsx(ChevronSmallDownIcon, { css: css `
              transition: transform 0.2s ease;
              transform: rotate(${isExpanded ? 180 : 0}deg);
            ` })] })), isExpanded && (_jsxs(_Fragment, { children: [_jsx("div", { css: css `
              margin-bottom: 8px;
            `, children: _jsx(StyledDropdown, { value: selectedCredits, onChange: onCreditsChange, size: "tiny", autoScrollToSelected: true, children: currentPrices === null || currentPrices === void 0 ? void 0 : currentPrices.map((price, index) => (_jsx(DropdownItem, { value: price.quantity, text: _jsxs(_Fragment, { children: [price.totalCredits, " Credits / ", isMonthly ? 'month' : 'year', ' ', index === 0 ? basePlanLabel : `(${price.discountPercent}% off)`] }), children: _jsxs(_Fragment, { children: [price.totalCredits, " Credits / ", isMonthly ? 'month' : 'year', ' ', index === 0 ? basePlanLabel : `(${price.discountPercent}% off)`] }) }, `price-${index}`))) }) }), _jsx("div", { css: css `
              margin-bottom: 16px;
            `, children: _jsx(StyledDropdown, { value: selectedSeats, onChange: onSeatsChange, size: "tiny", autoScrollToSelected: true, children: seatProductsSteps === null || seatProductsSteps === void 0 ? void 0 : seatProductsSteps.map((step, index) => (_jsx(DropdownItem, { value: step.quantity, text: _jsxs(_Fragment, { children: [step.quantity + baseSeatAmount, " seats included ", index === 0 ? basePlanLabel : ''] }), children: _jsxs(_Fragment, { children: [step.quantity + baseSeatAmount, " seats included ", index === 0 ? basePlanLabel : ''] }) }, `seat-step-${index}`))) }) }), _jsxs(Button, { css: css `
              width: 100%;
              margin-bottom: 8px;
            `, textVariant: "body-2-m", onClick: handleContinue, disabled: !selectedPrice, children: ["Continue to ", planName, " ", _jsx(ArrowRight, {})] }), _jsx("div", { css: css `
              margin-bottom: 24px;

              @media only screen and (max-height: 600px) {
                margin-bottom: 12px;
              }
            `, children: _jsx(Text, { variant: "body-3-r", color: colors.text_and_icons.secondary.DEFAULT, children: "Credits granted upfront" }) }), _jsxs("div", { css: css `
              gap: 6px;
              display: flex;
              flex-direction: column;
            `, children: [_jsx(Text, { variant: "body-3-r", children: featuresTitle }), _jsx("div", { css: css `
                gap: 6px;
                display: flex;
                flex-direction: column;
              `, children: planFeatures.map(({ feature, feature_description }, index) => {
                                    return (_jsxs("div", { css: css `
                      display: flex;
                    `, children: [_jsx(CheckIcon, { css: css `
                        margin-right: 8px;
                      `, color: colors.text_and_icons.positive.DEFAULT }), _jsx(Text, { variant: "body-3-r", color: colors.text_and_icons.secondary.DEFAULT, children: feature }), feature_description ? (_jsxs(_Fragment, { children: [_jsx(InfoDarkIcon, Object.assign({ css: css `
                            margin-left: 6px;
                          ` }, getTooltipProps({
                                                        tooltipId: `${tooltipId}${index}`,
                                                    }))), _jsx(Tooltip, { id: `${tooltipId}${index}`, children: _jsx("div", { css: css `
                              max-width: 200px;
                            `, children: feature_description }) })] })) : ('')] }, index));
                                }) })] })] }))] }));
};
export default PlanCard;
